import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { appModernizationDetails } from '../../../config';
import { CommonService } from '../shared/services/bulk-maintenance/common.service';
import { environment } from '../../../../environments/environment';
import * as XLSX from 'xlsx';
import { GlobalCacheService } from '../../services/global-cache.service';
import { maintenanceV4APIs } from '../../configV4';
import { BULK_MAINTENANCE_ERROR_MESSAGES } from '../shared/services/bulk-maintenance/bulk-maintenance-error-messages';
import { SharedAuthService } from '../../../shared/shared.auth.service';

@Injectable({
    providedIn: 'root'
})
export class ForecastBulkService {
    private configV4: any;
    private forecastBulkRequiredHeaders = [
        'Update Type',
        'ASK_Global_Id', 'service', 'year',
        'QNT_Jan', 'QNT_Feb', 'QNT_Mar', 'QNT_Apr',
        'QNT_May', 'QNT_Jun', 'QNT_Jul', 'QNT_Aug',
        'QNT_Sep', 'QNT_Oct', 'QNT_Nov', 'QNT_Dec',
    ];
    private allBulkHeaders = [
        'Update Type',
        'ASK_Global_Id', 'service', 'year',
        'QNT_Jan', 'QNT_Feb', 'QNT_Mar', 'QNT_Apr',
        'QNT_May', 'QNT_Jun', 'QNT_Jul', 'QNT_Aug',
        'QNT_Sep', 'QNT_Oct', 'QNT_Nov', 'QNT_Dec',
        'CY_Rate', 'Unit_Of_Measure', 'I_Flag',
        'LONG_APP_NAME', 'SV_Desc', 'Forecast_Cycle',
        'Service_Owner_Email', 'Service_Owner',
        'Service_Owner_MSID', 'Edit_TOH'
    ]

    private apiUrl: string;
    private config: any;
    private largeApiUrl: string = environment.LargeapiUrl;
   
    userDtls: any;
    constructor(private http: HttpClient, private commonService: CommonService, private cacheService: GlobalCacheService,
        private sharedAuthService: SharedAuthService
    ) {
        this.config = appModernizationDetails;
        this.apiUrl = environment.apiUrl;
        this.configV4 = maintenanceV4APIs;
        const currentUser = sessionStorage.getItem('currentUser') || '';
        this.userDtls = {
            cn: this.sharedAuthService.getMsid(),
            mail: sessionStorage.getItem('loggedInUserEmail') || '',
            givenName: currentUser ? currentUser.trim().split(',')[1].trim() : '',
            sn: currentUser ? currentUser.trim().split(',')[0].trim() : '',
        }
    }

    public modifyHeaders(headers: string[]) {
        const newHeaders = [];
        for (const item of headers) {
            let isMatch = false;
            for (const data of this.allBulkHeaders) {
                if (item?.trim().toLowerCase() == data.toLowerCase()) {
                    isMatch = true;
                    newHeaders.push(data);
                    break;
                }
            }
            if (!isMatch) {
                newHeaders.push(item);
            }
        }
        return newHeaders;
    }

    public modifyUploadedData(data: any[], headers: string[]) {
        const updatedData = [];
        for (const item of data) {
            const newData = {};
            for (const key of Object.keys(item)) {
                const header = headers.find(ele => ele.toLowerCase() == key?.trim().toLowerCase());
                newData[header] = item[key];
            }
            updatedData.push(newData);
        }

        return updatedData;
    }

    public checkRequiredHeaders(headers: string[]) {
        const missingHeader = this.forecastBulkRequiredHeaders.filter(item => !headers.includes(item));
        return missingHeader;
    }

    public compareData(item1: any, item2: any) {
        for (const key of this.forecastBulkRequiredHeaders) {
            if (item1[key] !== item2[key]) {
                return false;
            }
        }
        return true;
    }

    public findDuplicatesAndUniqueRecords(dataset: any[]) {
        const uniqueRecords = [];
        const duplicates = [];
        dataset.filter((data, index) => {
            if (
                index ===
                dataset.findIndex(
                    (o) => this.compareData(data, o)
                )
            ) {
                data['Status'] = 'Success';
                uniqueRecords.push(data);
            } else {
                data['Status'] = 'Fail';
                data['Message'] = BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.DUPLICATE_RECORD;
                uniqueRecords.push(data);
            }
        });
        return uniqueRecords
    }

    private validateMonthQuantity(ele: any, monthName: string): void {
        const fieldName = `QNT_${monthName}`;
        if (ele[fieldName]) {
            ele[fieldName] = isNaN(ele[fieldName]) ? ele[fieldName] : Number(ele[fieldName]);
            if (!Number.isInteger(ele[fieldName])) {
                ele['Status'] = 'Fail';
                const msg = `${fieldName}${BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.MONTH_INTEGER_SUFFIX}`;
                ele['Message'] = ele['Message'] ?
                    `${ele['Message']}, ${msg}` :
                    msg;
            }
        } else {
            ele[fieldName] = null;
        }
    }

    private validateRequiredFields(ele: any, index: number): void {

        let requiredFields = [
            { key: 'Update Type', message: BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.UPDATE_TYPE_EMPTY, nullCheck: true },
            { key: 'Update Type', message: BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.UPDATE_TYPE_INVALID, validCheck: true },
            { key: 'ASK_Global_Id', message: BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.ASK_GLOBAL_ID_REQUIRED, nullCheck: true },
            { key: 'service', message: BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.SERVICE_REQUIRED, nullCheck: true },
            { key: 'year', message: BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.YEAR_REQUIRED, nullCheck: true },
        ];
        let obj = this.commonService.initializeValidationObject();
        obj = this.commonService.validateFieldsAndUpdateType(requiredFields, ele, index, obj, ['U', 'A']);

        if (obj.status === false) {
            ele['Status'] = 'Fail';
            ele['Message'] = obj.message;
        } else {
            ele['Status'] = 'Success';
            ele['Message'] = '';
        }

    }

    private validateYear(ele: any, currentForecast: any): void {
        if (!Object.keys(currentForecast['Forecast_Months']).includes(ele['year'])) {
            ele['Status'] = 'Fail';
            const list = Object.keys(currentForecast['Forecast_Months']);
            const msg = `${BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.YEAR_INVALID_PREFIX}${list}`;
            ele['Message'] = ele['Message'] ?
                `${ele['Message']}, ${msg}` :
                msg;
        } else {
            ele['year'] = Number(ele['year']);
        }
    }

    private validateEditability(ele: any): void {
        if (['U', 'u'].includes(ele['Update Type']) && ele['Edit_TOH'] == 'No') {
            ele['Status'] = 'Fail';
            const msg = BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.NOT_EDITABLE;
            ele['Message'] = ele['Message'] ?
                `${ele['Message']}, ${msg}` :
                msg;
        }
    }

    public validateForecastAndUpdateType(ele: any, index: number, currentForecast: any) {
        ele['rowNum'] = index;
        ele['Forecast_Cycle'] = currentForecast['Forecast_Cycle'];

        this.validateRequiredFields(ele, index);
        // this.validateYear(ele, currentForecast);

        // Validate all month quantities
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        // months.forEach(month => this.validateMonthQuantity(ele, month));

        // this.validateEditability(ele);

        return ele;
    }

    public validateRecord(excelData: any[], currentForecast: any) {
        try {
            const clonedExcelData = excelData.map(el => ({ ...el }));
            const { uniques, duplicates } = this.commonService.findDuplicatesAndUniqueRecords(clonedExcelData, this.allBulkHeaders);
            const duplicateRecords = duplicates;
            let totalRecords = [];
            for (const [index, item] of uniques.entries()) {
                const validatedRecord = this.validateForecastAndUpdateType(item, index, currentForecast);
                let status = null;
                totalRecords.push(validatedRecord)
            }
            return { totalRecords, duplicateRecords }
        }
        catch (err) {
            console.error(BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.VALIDATION_FAILED_PREFIX, err);
            return err;
        }
    }

    private extractHeaders(sheet: XLSX.WorkSheet): string[] {
        const headers = [];
        const range = XLSX.utils.decode_range(sheet['!ref']);
        const R = range.s.r;

        for (let C = range.s.c; C <= range.e.c; ++C) {
            const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })];

            if (cell === undefined) {
                break;
            }

            let hdr = '';
            if (cell && cell.t) {
                hdr = XLSX.utils.format_cell(cell);
            }
            headers.push(hdr);
        }

        return headers;
    }

    private processExcelRows(excelRows: any[], headers: string[]): any[] {
        if (excelRows.length === 0) {
            return excelRows;
        }

        excelRows.forEach(row => {
            headers.forEach(hd => {
                if (row[hd] === undefined) {
                    row[hd] = '';
                }
            });
        });

        return excelRows;
    }

    private processWorkbook(binarystr: string): { excelRows: any[], headers: string[] } {
        const wb: XLSX.WorkBook = XLSX.read(binarystr, { type: 'binary' });
        const sheet_name_list = wb.SheetNames;
        const sheet = wb.Sheets[sheet_name_list[0]];

        const headers = this.extractHeaders(sheet);
        let excelRows = XLSX.utils.sheet_to_json(wb.Sheets[sheet_name_list[0]], { raw: false });
        excelRows = this.processExcelRows(excelRows, headers);

        return { excelRows, headers };
    }

    public convertCsvToArray(file: any): Promise<any> {
        const reader: FileReader = new FileReader();
        reader.readAsBinaryString(file);

        return new Promise((resolve, reject) => {
            reader.onload = (e: any) => {
                try {
                    const binarystr: string = e.target.result;
                    const result = this.processWorkbook(binarystr);

                    if (result.excelRows && result.headers) {
                        resolve(result);
                    } else {
                        reject(BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.UNEXPECTED_ERROR);
                    }
                } catch (error) {
                    reject(BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.FILE_PROCESSING_ERROR_PREFIX + error);
                }
            };

            reader.onerror = () => {
                reject(BULK_MAINTENANCE_ERROR_MESSAGES.FORECAST_BULK.FILE_READING_ERROR);
            };
        });
    }

    public fcBulkPreview(body: any): Observable<any> {
       
        body['userDetails'] = this.userDtls;
        const url = this.largeApiUrl + this.config.routerpath.fcServiceBulkPreview;
        const headers = new HttpHeaders({
            'Content-Type': ''
        });
        const options = {
            headers: headers
        }
        return this.http.post<any>(url, body, options);
    }

    public fcBulkSubmit(body: any): Observable<any> {
     
        body['userDetails'] = this.userDtls;
        // delete cache url data for filter API call
        const deleteUrl = this.apiUrl + this.configV4.getForecastFilters;
        this.cacheService.delete(deleteUrl);

        const url = this.largeApiUrl + this.config.routerpath.fcServiceBulkSubmit;
        const headers = new HttpHeaders({
            'Content-Type': ''
        });
        const options = {
            headers: headers
        }
        return this.http.put<any>(url, body, options);
    }

    public fcCurrentForecastYear(): Observable<any> {
        const url = this.apiUrl + this.config.routerpath.fcCurrentForecastYear;
        const headers = new HttpHeaders({
            'Content-Type': ''
        });
        const options = {
            headers: headers
        }
        return this.http.get<any>(url, options);
    }
}